import { extractJsonObject } from "./outlinePrompt.js";
import { newOutlineSection, normalizeOutlineRole, } from "./types.js";
const VISUAL_KINDS = new Set([
    "none",
    "image",
    "graphic",
    "chart",
    "table",
]);
function splitBodyToBullets(body) {
    const lines = body
        .split(/\n+/)
        .map((l) => l.replace(/^[\s•\-*]+\s*/, "").trim())
        .filter(Boolean);
    if (lines.length > 1)
        return lines;
    return body.trim() ? [body.trim()] : [];
}
function parseGraphic(raw) {
    if (!raw || typeof raw !== "object")
        return undefined;
    const o = raw;
    const template = String(o.template ?? "list").trim();
    const itemsRaw = Array.isArray(o.items) ? o.items : [];
    const items = itemsRaw
        .map((item) => {
        if (!item || typeof item !== "object")
            return null;
        const row = item;
        const label = String(row.label ?? "").trim();
        if (!label)
            return null;
        return {
            label,
            sublabel: String(row.sublabel ?? "").trim() || undefined,
            icon: String(row.icon ?? "").trim() || undefined,
        };
    })
        .filter((x) => x !== null);
    if (!items.length)
        return undefined;
    return { template, items };
}
function parseChart(raw) {
    if (!raw || typeof raw !== "object")
        return undefined;
    const o = raw;
    const type = String(o.type ?? "bar").trim();
    const seriesRaw = Array.isArray(o.series) ? o.series : [];
    const series = seriesRaw
        .map((s) => {
        if (!s || typeof s !== "object")
            return null;
        const row = s;
        const label = String(row.label ?? "").trim();
        const pointsRaw = Array.isArray(row.points) ? row.points : [];
        const points = pointsRaw
            .map((p) => {
            if (!p || typeof p !== "object")
                return null;
            const pt = p;
            const y = Number(pt.y);
            const x = String(pt.x ?? "").trim();
            if (!x || !Number.isFinite(y))
                return null;
            return { x, y };
        })
            .filter((x) => x !== null);
        if (!label || !points.length)
            return null;
        return { label, points };
    })
        .filter((x) => x !== null);
    if (!series.length)
        return undefined;
    return { type, series };
}
function parseTable(raw) {
    if (!raw || typeof raw !== "object")
        return undefined;
    const o = raw;
    const columns = Array.isArray(o.columns)
        ? o.columns.map((c) => String(c).trim()).filter(Boolean)
        : [];
    const rowsRaw = Array.isArray(o.rows) ? o.rows : [];
    const rows = rowsRaw
        .map((r) => (Array.isArray(r) ? r.map((c) => String(c).trim()) : []))
        .filter((r) => r.length > 0);
    if (!columns.length || !rows.length)
        return undefined;
    return { columns, rows };
}
function parseVisual(raw) {
    if (!raw || typeof raw !== "object")
        return undefined;
    const o = raw;
    const kindRaw = String(o.kind ?? "none").trim().toLowerCase();
    const kind = VISUAL_KINDS.has(kindRaw) ? kindRaw : "none";
    const visual = { kind };
    const caption = String(o.caption ?? "").trim();
    if (caption)
        visual.caption = caption;
    const imageSubject = String(o.imageSubject ?? "").trim();
    if (imageSubject)
        visual.imageSubject = imageSubject;
    const graphic = parseGraphic(o.graphic);
    if (graphic)
        visual.graphic = graphic;
    const chart = parseChart(o.chart);
    if (chart)
        visual.chart = chart;
    const table = parseTable(o.table);
    if (table)
        visual.table = table;
    if (kind === "chart" && !chart)
        return { kind: "image", imageSubject: imageSubject || caption };
    if (kind === "table" && !table)
        return { kind: "none" };
    if (kind === "graphic" && !graphic)
        return { kind: "image", imageSubject: imageSubject || caption };
    return visual;
}
function normalizeRichSlide(raw) {
    if (!raw || typeof raw !== "object")
        return null;
    const o = raw;
    const title = String(o.title ?? o.headline ?? "").trim();
    const subtitle = String(o.subtitle ?? "").trim() || undefined;
    let bullets = [];
    if (Array.isArray(o.bullets)) {
        bullets = o.bullets.map((b) => String(b).trim()).filter(Boolean);
    }
    const bodyRaw = String(o.body ?? o.summary ?? "").trim();
    if (!bullets.length && bodyRaw) {
        bullets = splitBodyToBullets(bodyRaw);
    }
    const role = normalizeOutlineRole(String(o.role ?? o.intent ?? "content"));
    const intent = String(o.intent ?? o.role ?? role).trim() || role;
    const notes = String(o.notes ?? "").trim() || undefined;
    const layoutHint = String(o.layoutHint ?? "").trim() || undefined;
    const visual = parseVisual(o.visual);
    const body = bullets.length && bodyRaw && !bullets.includes(bodyRaw) ? bodyRaw : undefined;
    if (!title && !bullets.length && !bodyRaw)
        return null;
    return newOutlineSection({
        title: title || "Slide",
        subtitle,
        bullets,
        body,
        notes,
        visual,
        layoutHint,
        role,
        intent,
    });
}
function normalizeLegacySection(raw) {
    const title = String(raw.title ?? raw.headline ?? "").trim();
    const body = String(raw.body ?? raw.summary ?? "").trim();
    const role = normalizeOutlineRole(String(raw.role ?? raw.intent ?? "content"));
    const intent = String(raw.intent ?? raw.purpose ?? role).trim();
    if (!title && !body)
        return null;
    return newOutlineSection({
        title: title || "Sezione",
        bullets: body ? splitBodyToBullets(body) : [],
        body: undefined,
        role,
        intent,
    });
}
/**
 * Salvage complete slide objects from a (possibly truncated) `slides` array.
 * Scans top-level `{...}` objects with string/escape awareness, parses each,
 * ignoring a trailing incomplete one. Recovers output cut by server char caps.
 */
function salvageSlidesFromTruncated(raw) {
    const key = raw.indexOf('"slides"');
    if (key < 0)
        return [];
    const arrStart = raw.indexOf("[", key);
    if (arrStart < 0)
        return [];
    const out = [];
    let depth = 0;
    let objStart = -1;
    let inStr = false;
    let esc = false;
    for (let i = arrStart + 1; i < raw.length; i += 1) {
        const ch = raw[i];
        if (inStr) {
            if (esc)
                esc = false;
            else if (ch === "\\")
                esc = true;
            else if (ch === '"')
                inStr = false;
            continue;
        }
        if (ch === '"')
            inStr = true;
        else if (ch === "{") {
            if (depth === 0)
                objStart = i;
            depth += 1;
        }
        else if (ch === "}") {
            depth -= 1;
            if (depth === 0 && objStart >= 0) {
                try {
                    const o = JSON.parse(raw.slice(objStart, i + 1));
                    const s = normalizeRichSlide(o);
                    if (s)
                        out.push(s);
                }
                catch {
                    /* skip malformed object */
                }
                objStart = -1;
            }
        }
        else if (ch === "]" && depth === 0) {
            break;
        }
    }
    return out;
}
/**
 * Parse outline da risposta F2-rev (`{ slides: [...] }`), Pulse legacy o array JSON.
 */
export function parseOutlineSections(raw) {
    const obj = extractJsonObject(raw);
    if (obj && Array.isArray(obj.slides)) {
        const sections = obj.slides
            .map(normalizeRichSlide)
            .filter((s) => s !== null);
        if (!sections.length) {
            throw new Error("JSON outline senza slide valide.");
        }
        return sections;
    }
    const t = raw.trim();
    const start = t.indexOf("[");
    const end = t.lastIndexOf("]");
    if (start >= 0 && end > start) {
        try {
            const parsed = JSON.parse(t.slice(start, end + 1));
            if (Array.isArray(parsed)) {
                const sections = parsed
                    .map((item) => item && typeof item === "object"
                    ? normalizeLegacySection(item)
                    : null)
                    .filter((s) => s !== null);
                if (sections.length)
                    return sections;
            }
        }
        catch {
            /* fallthrough */
        }
    }
    // Truncated output (server char cap) → recover the complete slides parsed so far.
    const salvaged = salvageSlidesFromTruncated(raw);
    if (salvaged.length)
        return salvaged;
    throw new Error(`Il modello non ha restituito JSON valido per l'outline (atteso { slides: [...] }). ` +
        `Lunghezza output: ${raw.length} char. Coda: ${raw.slice(-160).replace(/\s+/g, " ")}`);
}
/** Parse a single outline section from AI extract (`{ slide: {...} }` or bare object). */
export function parseSingleOutlineSection(raw) {
    const obj = extractJsonObject(raw);
    if (obj) {
        const candidate = obj.slide ?? obj.section ?? obj;
        if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
            const section = normalizeRichSlide(candidate);
            if (section)
                return section;
        }
    }
    throw new Error(`Il modello non ha restituito JSON valido per la sezione slide. Coda: ${raw.slice(-160).replace(/\s+/g, " ")}`);
}
